import { SectionCard } from "@/components/common/SectionCard";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgRepresentativeFullCostMode } from "@/lib/manufacturing/pricingNotificationTypes";
import type { ManufacturingPricingNotificationSettingsState } from "./useManufacturingPricingNotificationSettings";

const MODE_LABELS: Record<MfgRepresentativeFullCostMode, string> = {
  rolling_average: "Rolling average FullCost (default)",
  median: "Median FullCost in catalog sample",
  max_in_period: "Max FullCost in catalog sample",
  fixed_floor: "Fixed dollar floor",
  pinned_sku: "Pinned SKU FullCost",
};

function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return value.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

type Props = {
  state: ManufacturingPricingNotificationSettingsState;
};

export function ManufacturingPricingNotificationSettingsCard({ state }: Props) {
  const s = state;
  if (!s.draft) {
    return (
      <SectionCard title="Variance alert settings">
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          {s.listBusy ? "Loading settings…" : "Settings unavailable."}
        </div>
        {s.localError ? (
          <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {s.localError}
          </div>
        ) : null}
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Variance alert settings"
      description="Default rule: alert when |bucket variance| exceeds 5% of representative FullCost. Notifications are preview-only until Slice 6."
    >
      {s.localError ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {s.localError}
        </div>
      ) : null}
      <div className="grid gap-3">
        <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
          Global alert threshold (% of FullCost)
          <input
            className={`${inputClass} mt-1`}
            value={s.draft.alert_threshold_pct}
            onChange={(e) => s.setDraft((d) => (d ? { ...d, alert_threshold_pct: e.target.value } : d))}
          />
        </label>
        <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
          Representative FullCost mode
          <select
            className={`${inputClass} mt-1`}
            value={s.draft.representative_full_cost_mode}
            onChange={(e) =>
              s.setDraft((d) =>
                d
                  ? {
                      ...d,
                      representative_full_cost_mode: e.target.value as MfgRepresentativeFullCostMode,
                    }
                  : d,
              )
            }
          >
            {(Object.keys(MODE_LABELS) as MfgRepresentativeFullCostMode[]).map((mode) => (
              <option key={mode} value={mode}>
                {MODE_LABELS[mode]}
              </option>
            ))}
          </select>
        </label>
        {s.draft.representative_full_cost_mode === "fixed_floor" ? (
          <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
            Fixed floor (USD)
            <input
              className={`${inputClass} mt-1`}
              value={s.draft.fixed_floor_usd}
              onChange={(e) => s.setDraft((d) => (d ? { ...d, fixed_floor_usd: e.target.value } : d))}
            />
          </label>
        ) : null}
        {s.draft.representative_full_cost_mode === "pinned_sku" ? (
          <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
            Pinned SKU
            <select
              className={`${inputClass} mt-1`}
              value={s.draft.pinned_model_id}
              onChange={(e) => s.setDraft((d) => (d ? { ...d, pinned_model_id: e.target.value } : d))}
            >
              <option value="">Select build model…</option>
              {s.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.model_code} — {m.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-zinc-300 sm:col-span-2">
          <input
            type="checkbox"
            checked={s.draft.enable_assumed_pct_rule}
            onChange={(e) =>
              s.setDraft((d) => (d ? { ...d, enable_assumed_pct_rule: e.target.checked } : d))
            }
          />
          Enable secondary rule (% of bucket assumed value)
        </label>
        {s.draft.enable_assumed_pct_rule ? (
          <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
            Assumed-variance threshold (%)
            <input
              className={`${inputClass} mt-1`}
              value={s.draft.assumed_variance_threshold_pct}
              onChange={(e) =>
                s.setDraft((d) => (d ? { ...d, assumed_variance_threshold_pct: e.target.value } : d))
              }
            />
          </label>
        ) : null}
      </div>

      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">Per-bucket overrides</div>
          <button type="button" className={ghostBtnClass} onClick={s.addBucketOverride} disabled={s.saveBusy}>
            + Add override
          </button>
        </div>
        {s.draft.bucket_overrides.length === 0 ? (
          <div className="text-xs text-gray-500 dark:text-zinc-400">Optional — uses the global threshold when empty.</div>
        ) : (
          <ul className="space-y-2">
            {s.draft.bucket_overrides.map((row, index) => (
              <li key={`${row.bucket_id}-${index}`} className="flex flex-wrap items-end gap-2">
                <label className="block min-w-[180px] flex-1 text-xs font-medium text-gray-700 dark:text-zinc-300">
                  Bucket
                  <select
                    className={`${inputClass} mt-1`}
                    value={row.bucket_id}
                    onChange={(e) =>
                      s.setDraft((d) => {
                        if (!d) return d;
                        const next = [...d.bucket_overrides];
                        next[index] = { ...next[index]!, bucket_id: e.target.value };
                        return { ...d, bucket_overrides: next };
                      })
                    }
                  >
                    <option value="">Select bucket…</option>
                    {s.buckets.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} ({b.bucket_key})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block w-28 text-xs font-medium text-gray-700 dark:text-zinc-300">
                  Threshold %
                  <input
                    className={`${inputClass} mt-1`}
                    value={row.alert_threshold_pct}
                    onChange={(e) =>
                      s.setDraft((d) => {
                        if (!d) return d;
                        const next = [...d.bucket_overrides];
                        next[index] = { ...next[index]!, alert_threshold_pct: e.target.value };
                        return { ...d, bucket_overrides: next };
                      })
                    }
                  />
                </label>
                <button type="button" className={ghostBtnClass} onClick={() => s.removeBucketOverride(index)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" className={mfgPrimaryBtnClass} disabled={s.saveBusy} onClick={() => void s.saveSettings()}>
          {s.saveBusy ? "Saving…" : "Save settings"}
        </button>
      </div>

      <div className="mt-6 rounded-xl border border-gray-100 bg-gray-50/80 p-3 dark:border-zinc-700 dark:bg-zinc-800/40">
        <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">Would-alert preview</div>
        <p className="mt-1 text-xs text-gray-600 dark:text-zinc-400">
          Simulate a bucket variance against current settings. No notification is sent.
        </p>
        <div className="mt-3 grid gap-3">
          <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
            Plant
            <select
              className={`${inputClass} mt-1`}
              value={s.previewDraft.plant_id}
              onChange={(e) => s.setPreviewDraft((d) => ({ ...d, plant_id: e.target.value }))}
            >
              <option value="">Select plant…</option>
              {s.plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
            Bucket (optional)
            <select
              className={`${inputClass} mt-1`}
              value={s.previewDraft.bucket_id}
              onChange={(e) => s.setPreviewDraft((d) => ({ ...d, bucket_id: e.target.value }))}
            >
              <option value="">Global threshold</option>
              {s.buckets.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
            Hypothetical variance (USD)
            <input
              className={`${inputClass} mt-1`}
              value={s.previewDraft.bucket_variance_usd}
              onChange={(e) => s.setPreviewDraft((d) => ({ ...d, bucket_variance_usd: e.target.value }))}
            />
          </label>
          <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
            Bucket assumed (USD, optional)
            <input
              className={`${inputClass} mt-1`}
              value={s.previewDraft.bucket_assumed_usd}
              onChange={(e) => s.setPreviewDraft((d) => ({ ...d, bucket_assumed_usd: e.target.value }))}
            />
          </label>
        </div>
        <div className="mt-3">
          <button type="button" className={ghostBtnClass} disabled={s.previewBusy} onClick={() => void s.runPreview()}>
            {s.previewBusy ? "Previewing…" : "Run preview"}
          </button>
        </div>
        {s.previewResult ? (
          <div
            className={`mt-3 rounded-lg border px-3 py-2 text-sm ${
              s.previewResult.would_alert
                ? "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100"
                : "border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100"
            }`}
          >
            <div className="font-semibold">{s.previewResult.would_alert ? "Would alert" : "Would not alert"}</div>
            <div className="mt-1 text-xs">
              Representative FullCost: {formatUsd(s.previewResult.representative_full_cost_usd)} (
              {MODE_LABELS[s.previewResult.representative_full_cost_mode]}) · Threshold:{" "}
              {formatPct(s.previewResult.alert_threshold_pct)} ({formatUsd(s.previewResult.alert_threshold_dollars)})
              {s.previewResult.variance_ratio !== null
                ? ` · Variance ratio: ${formatPct(s.previewResult.variance_ratio)}`
                : null}
            </div>
            <div className="mt-1 text-xs opacity-80">{s.previewResult.representative_full_cost_note}</div>
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
}
